import { z } from 'zod';
import { CuidSchema } from './crm.schemas.js';
import { CHART_KINDS, DATE_GRAINS, REPORT_SOURCE_KEYS } from './reports.js';
import { REPORT_MODULES } from './dashboards.js';

/**
 * مخططات باني التقارير.
 *
 * التحقق هنا يقتصر على الشكل. مطابقة المصدر والتقسيم والقياس للكتالوج تتم
 * في الخادم، لأنها تعتمد على بعضها: تقسيم يخص مصدرًا لا يصلح لغيره.
 */

const key = z.string().trim().min(1).max(60);

export const ReportDefinitionSchema = z.object({
  source: z.enum(REPORT_SOURCE_KEYS as [string, ...string[]]),
  dimension: key,
  measure: key,
  chart: z.enum(CHART_KINDS).default('TABLE'),
  grain: z.enum(DATE_GRAINS).optional(),
  from: z.string().trim().max(30).nullable().optional(),
  to: z.string().trim().max(30).nullable().optional(),
  /** مفاتيح المرشّحات وقيمها؛ كلاهما يُطابَق بالكتالوج في الخادم. */
  filters: z.record(key, z.string().trim().max(120)).default({}),
  limit: z.coerce.number().int().min(1).max(200).default(20),
  sort: z.enum(['VALUE_DESC', 'VALUE_ASC', 'LABEL_ASC']).default('VALUE_DESC'),
});
export type ReportDefinitionInput = z.infer<typeof ReportDefinitionSchema>;

export const SaveReportSchema = z.object({
  name: z.string().trim().min(2, 'اسم التقرير مطلوب.').max(160),
  description: z.string().trim().max(600).optional(),
  /**
   * التقرير المشترك يظهر لكل الفريق.
   *
   * الخاص يبقى لصاحبه: التقارير التجريبية أثناء البحث عن رقم لا يجب أن
   * تُغرق قائمة الجميع.
   */
  isShared: z.boolean().default(false),
  /**
   * النشر: أين يظهر التقريرُ بطاقةً في لوحة.
   *
   * الوحدةُ تقول أيَّ لوحةٍ يخصّ، و«الرئيسية» تضعه في لوحة النظام كلِّه.
   * والمنشورُ مشتركٌ ضمنًا: لوحةٌ يراها الفريق لا تحمل تقريرًا خاصًّا.
   */
  module: z.enum(REPORT_MODULES).nullable().optional(),
  showOnHome: z.boolean().default(false),
  showOnModule: z.boolean().default(false),
  definition: ReportDefinitionSchema,
});
export type SaveReportInput = z.infer<typeof SaveReportSchema>;

export const SavedReportListQuerySchema = z.object({
  home: z.coerce.boolean().optional(),
  module: z.enum(REPORT_MODULES).optional(),
});
export type SavedReportListQuery = z.infer<typeof SavedReportListQuerySchema>;

export const ReportExportSchema = z.object({
  definition: ReportDefinitionSchema,
  title: z.string().trim().min(1).max(160).default('تقرير'),
  format: z.enum(['xlsx', 'csv']).default('xlsx'),
});
export type ReportExportInput = z.infer<typeof ReportExportSchema>;

export const UpdateReportSchema = SaveReportSchema.partial();
export type UpdateReportInput = z.infer<typeof UpdateReportSchema>;

export const ReportIdSchema = CuidSchema;
